import { TableMode } from 'core-components/library/list/types'
import { IProperties } from 'core-components/sitebuilder/propertygenerator'

interface IInitializeDataActions {
  rerender?: () => void
}

export interface IInitializeData {
  actions?: IInitializeDataActions
}

export interface IEditorTableProps {
  initializeWidgetData?: (data: IInitializeData) => void
  editRoles: string[]
  deleteRoles: string[]
  createRoles: string[]
}

export const getTableProperties = (): IProperties[] => {
  return [
    {
      name: 'source',
      type: 'string',
      needLoad: true,
      required: true,
      value: '',
    },
    {
      name: 'columns',
      type: 'custom',
      typeAlias: 'IColumns[]',
      customEditor: 'core-components/sitebuilder/propertygenerator/editor/Table/Columns',
      objectDescription: [
        { name: 'title', type: 'string', value: '' },
        { name: 'field', type: 'string', value: '' },
        { name: 'parentField', type: 'string', value: '' },
        {
          name: 'className',
          type: 'string',
          value: '',
          tooltipButtonModalContentPath:
            'core-components/sitebuilder/propertygenerator/components/tooltipButtons/table/ClassNameModal',
        },
        {
          name: 'renderType',
          type: 'string',
          value: 'default',
          enumValues: [
            { key: 'default', value: 'Default', description: 'Text from model' },
            { key: 'rawText', value: 'Raw data', description: 'Text with line breaks' },
            { key: 'number', value: 'Number', description: '1 456 789' },
            { key: 'percentage', value: 'Percentage', description: '69%' },

            { key: 'boolean', value: 'Boolean' },
            {
              key: 'booleanText',
              value: 'Boolean Text',
              description: 'true | false',
              parent: 'boolean',
            },
            {
              key: 'booleanNumber',
              value: 'Boolean Number',
              description: '1 | 0',
              parent: 'boolean',
            },

            { key: 'date', value: 'Date' },
            { key: 'dateText', value: 'Date Text', description: 'Aug 30th 2024', parent: 'date' },
            { key: 'dateTime', value: 'Date Time', description: '13:58', parent: 'date' },
            {
              key: 'dateFull',
              value: 'Date with time',
              description: 'Aug 30th 2024 | 13:58',
              parent: 'date',
            },

            { key: 'currency', value: 'Currency' },
            { key: 'currencyUSD', value: 'Currency (USD)', description: '69$', parent: 'currency' },
            {
              key: 'currencyEuro',
              value: 'Currency (Euro)',
              description: '69€',
              parent: 'currency',
            },
          ],
        },
        {
          name: 'renderFunction',
          type: 'string',
          needLoad: true,
          value: '',
        },
        {
          name: 'customFilter',
          type: 'string',
          needLoad: true,
          value: '',
        },
        {
          name: 'columnVisibilityRoles',
          type: 'custom',
          typeAlias: 'string[]',
          tooltip: 'Set up the roles of users who can see the column',
          customEditor:
            'core-components/sitebuilder/propertygenerator/editor/Table/roles/RolesEditor',
          value: [],
          allowSelectAll: true,
        },
        {
          name: 'withoutOperator',
          type: 'boolean',
          value: false,
        },
        {
          name: 'hideSettings',
          type: 'boolean',
          value: false,
        },
        {
          name: 'disableSort',
          type: 'boolean',
          value: false,
        },
      ],
      required: true,
      value: [],
    },
    {
      name: 'mode',
      type: 'enum', // dropdown
      enumValues: [
        { key: TableMode.table, value: TableMode.table },
        { key: TableMode.div, value: TableMode.div },
      ],
      value: TableMode.table,
    },
    {
      name: 'filter',
      type: 'custom',
      typeAlias: 'IDataProviderFilter',
      customEditor: 'core-components/sitebuilder/propertygenerator/editor/Table/filters/Filters',
      value: undefined,
    },
    {
      name: 'header',
      type: 'boolean',
      value: false,
    },
    {
      name: 'isHeaderSticky',
      type: 'boolean',
      value: false,
    },
    {
      name: 'pagination',
      type: 'boolean',
      value: false,
    },
    {
      name: 'useRowCheckbox',
      type: 'boolean',
      value: false,
    },
    {
      name: 'hasRefresh',
      type: 'boolean',
      value: false,
    },
    {
      name: 'hasHierarchy',
      type: 'boolean',
      value: false,
    },
    {
      name: 'hasExport',
      type: 'boolean',
      value: false,
    },
    {
      name: 'exportTooltip',
      type: 'string',
      value: '',
    },
    {
      name: 'exportColumns',
      type: 'array',
      objectDescription: [
        { name: 'title', type: 'string', value: '' },
        { name: 'field', type: 'string', value: '' },
      ],
      value: [],
    },
    {
      name: 'rowActions',
      type: 'string',
      needLoad: true,
      value: '',
    },
    {
      name: 'rowTpl',
      type: 'string',
      needLoad: true,
      value: '',
    },
    {
      name: 'cellTpl',
      type: 'string',
      needLoad: true,
      value: '',
    },
    {
      name: 'hierarchyTpl',
      type: 'string',
      needLoad: true,
      value: '',
    },
    {
      name: 'hierarchyContent',
      type: 'string',
      needLoad: true,
      value: '',
    },
    {
      name: 'className',
      type: 'string',
      value: '',
    },
    {
      name: 'placeholderContent',
      type: 'string',
      needLoad: true,
      value: '',
    },
    {
      name: 'classes',
      type: 'object',
      objectDescription: [
        { name: 'root', type: 'string', value: '' },
        { name: 'header', type: 'string', value: '' },
        { name: 'scrollRoot', type: 'string', value: '' },
      ],
      value: {},
    },
    {
      name: 'initializeWidgetData',
      type: 'string',
      tooltip: 'Callback for initialize component data or methods',
      needLoad: true,
      value: '',
    },
    {
      name: 'editRoles',
      type: 'custom',
      typeAlias: 'string[]',
      tooltip: 'Set up the roles of users who can edit items',
      customEditor: 'core-components/sitebuilder/propertygenerator/editor/Table/roles/RolesEditor',
      value: [],
    },
    {
      name: 'deleteRoles',
      type: 'custom',
      typeAlias: 'string[]',
      tooltip: 'Set up the roles of users who can delete items',
      customEditor: 'core-components/sitebuilder/propertygenerator/editor/Table/roles/RolesEditor',
      value: [],
    },
    {
      name: 'createRoles',
      type: 'custom',
      typeAlias: 'string[]',
      tooltip: 'Set up the roles of users who can create new items',
      customEditor: 'core-components/sitebuilder/propertygenerator/editor/Table/roles/RolesEditor',
      value: [],
    },
  ]
}
